
import { useEffect, useRef, useState } from "react";
import { formatValue } from "../utils/helpers";
import metricMetadataIcon from "../assets/metric-metadata-icon-slate.svg";
import MetricMetadataModal from "./MetricMetadataModal";

import {
    DisaggregatedMetric,
    DistributionData,
    GeoDetails,
    GeoTag,
    MetricMetadata,
    RawIndicatorData,
} from "../services/types";

import {
    fetchDistributionData,
    fetchIndicatorBinCount,
    fetchMetricMetadata,
    indexMetricIds,
} from "../utils/dashboardUtils";

import { useCountyFips } from "../context/CountyFipsContext";
import { getIconByName } from "../utils/icons";

interface TripleMetricSectionProps {
    title: string;
    metricOne: DisaggregatedMetric;
    metricTwo: DisaggregatedMetric;
    metricThree: DisaggregatedMetric;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    unitDescriptor?: string;
    svg?: string;
    showMetadataIcon?: boolean;
}

interface TripleMetricItemProps {
    metric: DisaggregatedMetric;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon: boolean;
}

// Each metric maintains its own metadata and modal state.
const TripleMetricItem = ({
    metric,
    disaggData,
    geoDetails,
    geoTag,
    selectionActive,
    svg,
    showMetadataIcon,
}: TripleMetricItemProps) => {
    const { countyFips } = useCountyFips();

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] =
        useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] =
        useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] =
        useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const { id, name, versions } = metric;
    const { value, unit, percent_calc } = versions[0];

    const formattedValue =
        unit === "ratio"
            ? `${percent_calc?.n}/${percent_calc?.d}`
            : value;

    const isIndexMetric = indexMetricIds.includes(id);

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id}`;

    const clickDebounceRef =
        useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache =
        useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(
        key: string,
        fetchFn: () => Promise<T>
    ): Promise<T> {
        if (fetchCache.current[key]) {
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    const geoKey = geoDetails.map(d => d.lod).join(",");
    const distKey =
        `distData-${id}-${geoKey}-${geoTag?.type}-${geoTag?.value}`;
    const countyKey = `countyDist-${id}-${countyFips}`;

    // Prefetch metadata and distribution data.
    useEffect(() => {
        if (!showMetadataIcon) return;

        let active = true;

        fetchWithCache(
            `metadata-${id}`,
            () => fetchMetricMetadata(id)
        )
            .then(res => {
                if (active) setMetadata(res?.data ?? null);
            })
            .catch(console.error);

        fetchWithCache(
            distKey,
            () => fetchIndicatorBinCount(id, geoDetails, geoTag)
        )
            .then(res => {
                if (active) setDistributionData(res ?? null);
            })
            .catch(console.error);

        fetchWithCache(
            countyKey,
            () => fetchDistributionData(id, [], countyFips)
        )
            .then(res => {
                if (active) {
                    setCountyDistributionData(res ?? null);
                }
            })
            .catch(console.error);

        return () => {
            active = false;
        };
    }, [id, distKey, countyKey, showMetadataIcon]);

    // Open the metadata modal, fetching missing data if needed.
    const handleClick = () => {
        if (clickDebounceRef.current) {
            clearTimeout(clickDebounceRef.current);
        }

        clickDebounceRef.current = setTimeout(async () => {
            // Toggle the modal.
            if (showMetadata) {
                setShowMetadata(false);
                return;
            }

            setShowMetadata(true);
            setLoading(true);
            setError(null);

            try {
                const [
                    metadataResponse,
                    distributionResponse,
                    countyDistributionResponse,
                ] = await Promise.all([
                    metadata
                        ? Promise.resolve({ data: metadata })
                        : fetchWithCache(
                            `metadata-${id}`,
                            () => fetchMetricMetadata(id)
                        ),

                    distributionData
                        ? Promise.resolve(distributionData)
                        : fetchWithCache(
                            distKey,
                            () => fetchIndicatorBinCount(
                                id,
                                geoDetails,
                                geoTag
                            )
                        ),

                    countyDistributionData
                        ? Promise.resolve(countyDistributionData)
                        : fetchWithCache(
                            countyKey,
                            () => fetchDistributionData(
                                id,
                                [],
                                countyFips
                            )
                        ),
                ]);

                setMetadata(metadataResponse?.data ?? null);
                setDistributionData(distributionResponse ?? null);
                setCountyDistributionData(
                    countyDistributionResponse ?? null
                );
            } catch (err) {
                const message =
                    err instanceof Error
                        ? err.message
                        : "An unexpected error occurred.";

                console.error("Error fetching metadata:", err);
                setError(
                    new Error(`Failed to load data: ${message}`)
                );
            } finally {
                setLoading(false);
            }
        }, 150);
    };

    useEffect(() => {
        return () => {
            if (clickDebounceRef.current) {
                clearTimeout(clickDebounceRef.current);
            }
        };
    }, []);

    return (
        <div className="relative min-w-0">
            {/* Value and metadata button */}
            <div className="flex items-start justify-between gap-2 mb-1">
                
                {/* Metric name, descriptor, and index badge */}
            <div className="space-y-0.5">
                <span className="text-[1.75rem] font-semibold leading-none">
                    {formatValue(formattedValue, unit, id)}
                </span>

                <h3 className="text-xs font-semibold text-port-text">
                    {name}
                </h3>

                {isIndexMetric && (
                    <div className="rounded-lg bg-gresham-slate h-4 w-10 px-1.5 text-[10px] text-white text-center">
                        Index
                    </div>
                )}
            </div>

                {showMetadataIcon && (
                    <button
                        ref={triggerRef}
                        type="button"
                        onClick={handleClick}
                        aria-haspopup="dialog"
                        aria-expanded={showMetadata}
                        aria-controls={modalId}
                        aria-label={`Additional information about ${name}`}
                        className="
                            inline-flex h-6 w-6 flex-shrink-0
                            items-start justify-center rounded
                            transition-transform
                            hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2
                            focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900
                            focus-visible:ring-offset-white
                            dark:focus-visible:ring-white
                            dark:focus-visible:ring-offset-neutral-900
                        "
                    >
                        <img
                            src={metricMetadataIcon}
                            alt=""
                            aria-hidden="true"
                            className="h-3 w-3"
                            draggable="false"
                        />
                    </button>
                )}
            </div>

            {/* Individual metric metadata modal */}
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};

// Shared section layout.
const TripleMetricSection = ({
    title,
    metricOne,
    metricTwo,
    metricThree,
    disaggData,
    geoDetails,
    geoTag,
    selectionActive,
    svg,
    unitDescriptor,
    showMetadataIcon = true,
}: TripleMetricSectionProps) => {
    const metrics = [metricOne, metricTwo, metricThree];

    // Check if any metric is an index indicator
    const isIndexMetric = metrics.some(metric =>
        indexMetricIds.includes(metric.id)
    );

    const sharedSvg = svg || getIconByName(metricOne.name);

    return (
        <section className="w-full">
            <div
                className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white"
                style={{
                    borderLeftWidth: isIndexMetric ? "4px" : undefined,
                    borderLeftColor: isIndexMetric ? "#3B5C67" : undefined,
                }}
            >
                <div className="p-3">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex-shrink-0 rounded-lg bg-gresham-slate/10 p-2 group-hover:bg-gresham-slate/20 transition-colors">
                            <img
                                src={sharedSvg}
                                alt={`${title} icon`}
                                className="w-8 h-8 text-blue-500"
                            />
                        </div>
                        <div className="space-y-0.5">
                            <h2 className="text-sm font-semibold text-port-text">
                                {title}
                            </h2>

                            {unitDescriptor && (
                                <p className="text-xs text-port-text leading-tight">
                                    {unitDescriptor}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Three independent metrics */}
                    <div className="grid grid-cols-3 gap-4">
                        {metrics.map(metric => (
                            <TripleMetricItem
                                key={metric.id}
                                metric={metric}
                                disaggData={disaggData}
                                geoDetails={geoDetails}
                                geoTag={geoTag}
                                selectionActive={selectionActive}
                                svg={sharedSvg}
                                showMetadataIcon={showMetadataIcon}
                            />
                        ))}
                    </div>
                </div>

                {/* Shared hover gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </div>
        </section>
    );
};

export default TripleMetricSection;
